import React, { useCallback, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Download, CheckSquare, Square, ChevronRight, Loader2, AlertTriangle } from 'lucide-react';
import { Negociacao, Pipeline, Profile } from '../../../types';
import { downloadWorkbook, fileStamp } from '../../../src/lib/excelExport';
import { ABAS, AbaKey, CAMPOS, GRUPOS, montarAbas } from './export/buildSheets';
import { carregarDadosExport, Dataset } from './export/fetchExportData';
import { useAuth } from '../../../src/contexts/AuthContext';
import { logUsageEvent } from '../../../src/lib/logger';

// O export busca SEMPRE os sete conjuntos: as contagens e a condição comercial da
// aba principal dependem das UCs, e ligar/desligar dataset conforme a aba deixaria
// colunas em branco sem explicação. São ~18 mil linhas na base inteira — cabe numa
// leitura só, feita no clique e não no carregamento do board.
const DATASETS: Dataset[] = ['extras', 'ucs', 'notas', 'analises', 'avalistas', 'tarefas', 'logs'];

const TODAS_AS_ABAS = new Set<AbaKey>(ABAS.map(a => a.key));

type Preset = 'completo' | 'politica' | 'basico';

const PRESETS: { key: Preset; label: string; ajuda: string }[] = [
    { key: 'completo', label: 'Completo', ajuda: 'Todas as colunas e todas as abas' },
    { key: 'politica', label: 'Fechamento do mês', ajuda: 'Público, plano, desconto, fidelidade, carência e multiplicador' },
    { key: 'basico', label: 'Básico', ajuda: 'As colunas essenciais da negociação' },
];

const selecaoDoPreset = (preset: Preset): { campos: Set<string>; abas: Set<AbaKey> } => {
    if (preset === 'politica') {
        return { campos: new Set(CAMPOS.filter(c => c.politica).map(c => c.key)), abas: new Set<AbaKey>(['negociacoes']) };
    }
    if (preset === 'basico') {
        return { campos: new Set(CAMPOS.filter(c => c.padrao).map(c => c.key)), abas: new Set<AbaKey>(['negociacoes', 'ucs']) };
    }
    return { campos: new Set(CAMPOS.map(c => c.key)), abas: new Set(TODAS_AS_ABAS) };
};

const alternar = <T,>(set: Set<T>, valor: T): Set<T> => {
    const proximo = new Set(set);
    if (proximo.has(valor)) proximo.delete(valor);
    else proximo.add(valor);
    return proximo;
};

interface Props {
    isOpen: boolean;
    onClose: () => void;
    negociacoes: Negociacao[];
    profiles: Profile[];
    pipelines: Pipeline[];
}

export default function ExportExcelModal({ isOpen, onClose, negociacoes, profiles, pipelines }: Props) {
    const { user } = useAuth();
    const inicial = useMemo(() => selecaoDoPreset('completo'), []);
    const [campos, setCampos] = useState<Set<string>>(inicial.campos);
    const [abas, setAbas] = useState<Set<AbaKey>>(inicial.abas);
    const [abertos, setAbertos] = useState<Set<string>>(() => new Set(['Política comercial']));
    const [progresso, setProgresso] = useState<{ pct: number; etapa: string } | null>(null);
    const [erro, setErro] = useState<string | null>(null);

    const aplicarPreset = useCallback((preset: Preset) => {
        const { campos: c, abas: a } = selecaoDoPreset(preset);
        setCampos(c);
        setAbas(a);
    }, []);

    const alternarGrupo = useCallback((grupo: string) => {
        const chaves = CAMPOS.filter(c => c.grupo === grupo).map(c => c.key);
        setCampos(atual => {
            const proximo = new Set(atual);
            const todosMarcados = chaves.every(k => atual.has(k));
            chaves.forEach(k => (todosMarcados ? proximo.delete(k) : proximo.add(k)));
            return proximo;
        });
    }, []);

    const exportar = useCallback(async () => {
        setErro(null);
        setProgresso({ pct: 0, etapa: 'iniciando' });
        logUsageEvent({ userId: user?.id, modulo: 'crm', funcionalidade: 'funil', acao: 'exportar_planilha', origem: 'acao' });
        try {
            const ids = negociacoes.map(n => Number(n.id)).filter(Number.isFinite);
            const dados = await carregarDadosExport(ids, new Set(DATASETS), (feito, total, etapa) => {
                setProgresso({ pct: total > 0 ? Math.round((feito / total) * 100) : 100, etapa });
            });

            // XLSX.writeFile é síncrono e trava a aba enquanto serializa: cede um
            // frame para o "montando a planilha" chegar a pintar antes do bloqueio.
            await new Promise(resolve => setTimeout(resolve, 30));

            const sheets = montarAbas({
                negociacoes: negociacoes as any,
                dados,
                profiles,
                pipelines,
                camposSelecionados: campos,
                abasSelecionadas: abas,
            });
            if (sheets.length === 0) {
                setErro('Nenhuma aba com conteúdo — selecione ao menos uma aba e uma coluna.');
                setProgresso(null);
                return;
            }
            downloadWorkbook(sheets, `CRM_Negociacoes_${fileStamp()}.xlsx`);
            setProgresso(null);
            onClose();
        } catch (e: any) {
            console.error('[ExportExcel] falha ao exportar:', e);
            setErro(e?.message || 'Não foi possível gerar a planilha.');
            setProgresso(null);
        }
    }, [negociacoes, profiles, pipelines, campos, abas, onClose, user]);

    if (!isOpen) return null;

    const carregando = progresso !== null;
    const semColunas = abas.has('negociacoes') && campos.size === 0 && abas.size === 1;

    return createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm"
                onClick={carregando ? undefined : onClose}
                style={{ animation: 'fadeIn .2s ease' }}
            />
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col animate-fade-in">
                {/* Header */}
                <div className="flex items-start justify-between p-5 border-b border-gray-100 dark:border-gray-700">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Exportar para Excel</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                            {negociacoes.length.toLocaleString('pt-BR')} negociação{negociacoes.length !== 1 ? 'ões' : ''} filtrada
                            {negociacoes.length !== 1 ? 's' : ''} · UCs, notas, termos e análise vêm junto
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={carregando}
                        className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-40"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto p-5 space-y-5">
                    {/* Presets */}
                    <div className="flex flex-wrap items-center gap-2">
                        {PRESETS.map(p => (
                            <button
                                key={p.key}
                                onClick={() => aplicarPreset(p.key)}
                                title={p.ajuda}
                                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-primary hover:text-primary transition-colors"
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>

                    {/* Abas */}
                    <div>
                        <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">Abas da planilha</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                            {ABAS.map(aba => (
                                <button
                                    key={aba.key}
                                    onClick={() => setAbas(atual => alternar(atual, aba.key))}
                                    title={aba.descricao}
                                    className={`flex items-start gap-2 px-3 py-2 rounded-lg text-sm text-left transition-all border ${
                                        abas.has(aba.key)
                                            ? 'bg-primary/10 text-primary font-medium border-primary/20'
                                            : 'bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400 border-transparent hover:border-gray-200 dark:hover:border-gray-600'
                                    }`}
                                >
                                    {abas.has(aba.key) ? (
                                        <CheckSquare size={14} className="flex-shrink-0 mt-0.5" />
                                    ) : (
                                        <Square size={14} className="flex-shrink-0 mt-0.5 text-gray-300 dark:text-gray-600" />
                                    )}
                                    <span className="min-w-0">
                                        <span className="block truncate">{aba.nome}</span>
                                        <span className="block text-[11px] font-normal opacity-70 truncate">{aba.descricao}</span>
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Colunas da aba Negociações */}
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">Colunas da aba Negociações</p>
                            <div className="flex items-center gap-3 text-xs font-medium">
                                <button
                                    onClick={() => setCampos(new Set(CAMPOS.map(c => c.key)))}
                                    className="text-primary hover:underline"
                                >
                                    Todas
                                </button>
                                <button
                                    onClick={() => setCampos(new Set())}
                                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:underline"
                                >
                                    Limpar
                                </button>
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            {GRUPOS.map(grupo => {
                                const doGrupo = CAMPOS.filter(c => c.grupo === grupo);
                                const marcados = doGrupo.filter(c => campos.has(c.key)).length;
                                const aberto = abertos.has(grupo);
                                return (
                                    <div key={grupo} className="border border-gray-100 dark:border-gray-700 rounded-lg overflow-hidden">
                                        <div className="flex items-center bg-gray-50 dark:bg-gray-700/40">
                                            <button
                                                onClick={() => setAbertos(atual => alternar(atual, grupo))}
                                                className="flex-1 flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 text-left"
                                            >
                                                <ChevronRight
                                                    size={14}
                                                    className={`flex-shrink-0 transition-transform ${aberto ? 'rotate-90' : ''}`}
                                                />
                                                <span>{grupo}</span>
                                                <span
                                                    className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${
                                                        marcados === doGrupo.length
                                                            ? 'bg-primary/10 text-primary'
                                                            : marcados === 0
                                                              ? 'bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-300'
                                                              : 'bg-secondary/20 text-secondary'
                                                    }`}
                                                >
                                                    {marcados}/{doGrupo.length}
                                                </span>
                                            </button>
                                            <button
                                                onClick={() => alternarGrupo(grupo)}
                                                className="px-3 py-2 text-[11px] font-medium text-gray-400 hover:text-primary"
                                            >
                                                {marcados === doGrupo.length ? 'Limpar' : 'Marcar'}
                                            </button>
                                        </div>
                                        {aberto && (
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 p-2">
                                                {doGrupo.map(campo => (
                                                    <button
                                                        key={campo.key}
                                                        onClick={() => setCampos(atual => alternar(atual, campo.key))}
                                                        className={`flex items-center gap-2 px-2.5 py-1.5 rounded-md text-[13px] text-left transition-all ${
                                                            campos.has(campo.key)
                                                                ? 'bg-primary/10 text-primary font-medium'
                                                                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                                        }`}
                                                    >
                                                        {campos.has(campo.key) ? (
                                                            <CheckSquare size={13} className="flex-shrink-0" />
                                                        ) : (
                                                            <Square size={13} className="flex-shrink-0 text-gray-300 dark:text-gray-600" />
                                                        )}
                                                        <span className="truncate">{campo.label}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {erro && (
                        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
                            <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                            <span>{erro}</span>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-100 dark:border-gray-700 p-5">
                    {progresso && (
                        <div className="mb-3">
                            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                                <span>Carregando {progresso.etapa}…</span>
                                <span>{progresso.pct}%</span>
                            </div>
                            <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                <div className="h-full bg-primary transition-all" style={{ width: `${progresso.pct}%` }} />
                            </div>
                        </div>
                    )}
                    <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                            {campos.size} de {CAMPOS.length} colunas · {abas.size} aba{abas.size !== 1 ? 's' : ''}
                        </span>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={onClose}
                                disabled={carregando}
                                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-40"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={exportar}
                                disabled={carregando || semColunas || abas.size === 0 || negociacoes.length === 0}
                                className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold rounded-lg transition-colors shadow-sm"
                            >
                                {carregando ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
                                {carregando ? 'Gerando…' : 'Exportar'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
